import { createContext, useContext, useState, useCallback, useMemo, useRef, ReactNode } from 'react';

type SidebarType = 'account' | 'opportunity' | 'lead' | 'subscriber' | 'contact' | 'meetingReport' | 'appointment' | null;

interface SidebarState {
  type: SidebarType;
  entityId: string | null;
  /** Opened by a « Modifier »: the sheet starts on its edit form (the contact's). */
  edit?: boolean;
}

/**
 * What may hold a sheet change back: an edit not saved in the sheet shown. Given the change, it
 * answers true when it takes it over (it asks first, then runs it or not), false to let it go.
 */
export type LeaveGuard = (proceed: () => void) => boolean;

export interface SidebarContextType {
  sidebarState: SidebarState;
  openAccountSidebar: (accountId: string) => void;
  openOpportunitySidebar: (opportunityId: string) => void;
  openLeadSidebar: (leadId: string) => void;
  openSubscriberSidebar: (subscriberId: string) => void;
  openContactSidebar: (contactId: string, options?: { edit?: boolean }) => void;
  openMeetingReportSidebar: (reportId: string) => void;
  openAppointmentSidebar: (appointmentId: string) => void;
  closeSidebar: () => void;
  /** The sheet shown holds an edit not saved: every change of sheet asks it first. Returns the release. */
  registerLeaveGuard: (guard: LeaveGuard) => () => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export function SidebarProvider({ children }: { children: ReactNode }) {
  const [sidebarState, setSidebarState] = useState<SidebarState>({
    type: null,
    entityId: null,
  });

  // An edit not saved is never dropped by a sheet opened from elsewhere (a search result, a name in
  // the Bureau): the sheet holding it asks first (relecture du 13/10/2026).
  const leaveGuard = useRef<LeaveGuard | null>(null);
  const show = useCallback((next: SidebarState) => {
    const proceed = () => setSidebarState(next);
    if (leaveGuard.current?.(proceed)) return;
    proceed();
  }, []);
  const registerLeaveGuard = useCallback((guard: LeaveGuard) => {
    leaveGuard.current = guard;
    return () => {
      if (leaveGuard.current === guard) leaveGuard.current = null;
    };
  }, []);

  const openAccountSidebar = useCallback((accountId: string) => {
    show({ type: 'account', entityId: accountId });
  }, [show]);

  const openOpportunitySidebar = useCallback((opportunityId: string) => {
    show({ type: 'opportunity', entityId: opportunityId });
  }, [show]);

  const openLeadSidebar = useCallback((leadId: string) => {
    show({ type: 'lead', entityId: leadId });
  }, [show]);

  const openSubscriberSidebar = useCallback((subscriberId: string) => {
    show({ type: 'subscriber', entityId: subscriberId });
  }, [show]);

  const openContactSidebar = useCallback((contactId: string, options?: { edit?: boolean }) => {
    show({ type: 'contact', entityId: contactId, edit: options?.edit === true });
  }, [show]);

  const openMeetingReportSidebar = useCallback((reportId: string) => {
    show({ type: 'meetingReport', entityId: reportId });
  }, [show]);

  const openAppointmentSidebar = useCallback((appointmentId: string) => {
    show({ type: 'appointment', entityId: appointmentId });
  }, [show]);

  const closeSidebar = useCallback(() => {
    show({ type: null, entityId: null });
  }, [show]);

  const contextValue = useMemo(() => ({
    sidebarState,
    openAccountSidebar,
    openOpportunitySidebar,
    openLeadSidebar,
    openSubscriberSidebar,
    openContactSidebar,
    openMeetingReportSidebar,
    openAppointmentSidebar,
    closeSidebar,
    registerLeaveGuard,
  }), [sidebarState, openAccountSidebar, openOpportunitySidebar, openLeadSidebar, openSubscriberSidebar, openContactSidebar, openMeetingReportSidebar, openAppointmentSidebar, closeSidebar, registerLeaveGuard]);

  return (
    <SidebarContext.Provider value={contextValue}>
      {children}
    </SidebarContext.Provider>
  );
}

/**
 * The sheets. `{ optional: true }` answers null outside the admin's layout (a card rendered alone,
 * in a test or a preview) instead of throwing.
 */
export function useSidebar(): SidebarContextType;
export function useSidebar(options: { optional: true }): SidebarContextType | null;
export function useSidebar(options?: { optional: true }): SidebarContextType | null {
  const context = useContext(SidebarContext);
  if (!context && !options?.optional) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context ?? null;
}
